import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, IDR, formatErr, NUM } from "@/lib/api";
import { canEditProduction, useAuth } from "@/lib/auth";

export default function MixDesignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id;
  const [form, setForm] = useState({ code: "", name: "", product_id: "", version: 1,
    target_output: 100, output_unit: "pcs", notes: "", status: "draft", items: [] });
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: prods }, { data: mats }] = await Promise.all([api.get("/products"), api.get("/materials")]);
        setProducts(prods); setMaterials(mats);
        if (!isNew) {
          const { data: md } = await api.get(`/mix-designs/${id}`);
          setForm({ ...md });
        }
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { material_id: "", quantity: 0, unit: "", waste_factor: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));

  const totalCost = useMemo(() => {
    return (form.items || []).reduce((sum, it) => {
      const m = materials.find((x) => x.id === it.material_id);
      const qty = Number(it.quantity || 0) * (1 + Number(it.waste_factor || 0));
      return sum + qty * Number(m?.unit_price || 0);
    }, 0);
  }, [form.items, materials]);

  const perUnit = totalCost / Math.max(1, Number(form.target_output || 1));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          material_id: it.material_id,
          quantity: Number(it.quantity || 0),
          unit: it.unit || (materials.find((m) => m.id === it.material_id)?.unit ?? ""),
          waste_factor: Number(it.waste_factor || 0),
          note: it.note,
        })),
      };
      if (isNew) {
        const { data } = await api.post("/mix-designs", payload);
        toast.success("Formula tersimpan");
        navigate(`/app/mix-designs/${data.id}`);
      } else {
        await api.patch(`/mix-designs/${id}`, payload);
        toast.success("Formula diperbarui");
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  const readonly = !canEditProduction(user);

  return (
    <form onSubmit={submit} className="space-y-5">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <div>
        <span className="gc-label">Mix Design</span>
        <h1 className="gc-h1 mt-1">{isNew ? "Formula Baru" : `Formula ${form.code}`}</h1>
      </div>

      <div className="gc-card grid md:grid-cols-2 gap-4">
        <div>
          <label className="gc-label block mb-1.5">Kode Formula *</label>
          <input required className="gc-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="md-code" />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Nama Formula *</label>
          <input required className="gc-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="md-name" />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Produk *</label>
          <select required className="gc-input" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="md-product">
            <option value="">— Pilih —</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="gc-label block mb-1.5">Versi</label>
            <input type="number" min={1} className="gc-input" value={form.version} onChange={(e) => setForm({ ...form, version: Number(e.target.value) })} />
          </div>
          <div>
            <label className="gc-label block mb-1.5">Target/Batch *</label>
            <input type="number" min={1} required className="gc-input" value={form.target_output} onChange={(e) => setForm({ ...form, target_output: Number(e.target.value) })} data-testid="md-target" />
          </div>
          <div>
            <label className="gc-label block mb-1.5">Satuan</label>
            <input className="gc-input" value={form.output_unit} onChange={(e) => setForm({ ...form, output_unit: e.target.value })} placeholder="pcs" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="gc-label block mb-1.5">Catatan Teknis</label>
          <textarea className="gc-textarea" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Catatan hasil uji lab, faktor air-semen, dsb." />
        </div>
      </div>

      <div className="gc-card">
        <div className="flex items-center justify-between">
          <div>
            <span className="gc-label">Komposisi Bahan</span>
            <h3 className="gc-h3 mt-1">Rincian material standar</h3>
          </div>
          {!readonly && (
            <button type="button" onClick={addItem} className="gc-btn-ghost" data-testid="md-add-item"><Plus className="w-4 h-4" /> Tambah Bahan</button>
          )}
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 pr-3">Bahan</th>
                <th className="text-right px-2">Qty</th>
                <th className="text-left px-2">Satuan</th>
                <th className="text-right px-2">Waste %</th>
                <th className="text-right px-2">Harga</th>
                <th className="text-right px-2">Subtotal</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.items?.map((it, idx) => {
                const m = materials.find((x) => x.id === it.material_id);
                const subtotal = Number(it.quantity || 0) * (1 + Number(it.waste_factor || 0)) * Number(m?.unit_price || 0);
                return (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="py-2 pr-3">
                      <select value={it.material_id} onChange={(e) => setItem(idx, { material_id: e.target.value, unit: materials.find((x) => x.id === e.target.value)?.unit || "" })} className="gc-input" data-testid={`md-item-mat-${idx}`}>
                        <option value="">— Pilih Bahan —</option>
                        {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                      </select>
                    </td>
                    <td className="px-2"><input type="number" step="any" className="gc-input text-right" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} data-testid={`md-item-qty-${idx}`} /></td>
                    <td className="px-2"><input className="gc-input" value={it.unit || m?.unit || ""} onChange={(e) => setItem(idx, { unit: e.target.value })} /></td>
                    <td className="px-2"><input type="number" step="0.01" className="gc-input text-right" value={it.waste_factor} onChange={(e) => setItem(idx, { waste_factor: e.target.value })} placeholder="0.03" /></td>
                    <td className="px-2 text-right text-slate-500">{IDR(m?.unit_price || 0)}</td>
                    <td className="px-2 text-right font-semibold">{IDR(subtotal)}</td>
                    <td className="px-2"><button type="button" onClick={() => removeItem(idx)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" data-testid={`md-item-del-${idx}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
              {!form.items?.length && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-sm">Belum ada bahan. Tekan “Tambah Bahan”.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
            <div className="gc-label !text-emerald-700">Estimasi Biaya Bahan / Batch</div>
            <div className="font-cabinet text-2xl font-black text-emerald-700 mt-1">{IDR(totalCost)}</div>
          </div>
          <div className="rounded-2xl bg-slate-900 text-white p-4">
            <div className="gc-label !text-emerald-300">HPP Bahan per Unit</div>
            <div className="font-cabinet text-2xl font-black mt-1">{IDR(perUnit)}</div>
            <div className="text-[10px] text-slate-300 mt-1">Target: {NUM(form.target_output, 0)} {form.output_unit}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="gc-label">Status</div>
            <div className="text-sm text-slate-700 mt-1">{form.status || "draft"}</div>
            <div className="text-[11px] text-slate-400 mt-2">Estimasi ini hanya mencakup biaya bahan. Tenaga kerja, overhead, dan biaya lingkungan dihitung pada tahap batch produksi.</div>
          </div>
        </div>
      </div>

      {!readonly && (
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate(-1)} className="gc-btn-ghost">Batal</button>
          <button type="submit" disabled={saving} className="gc-btn-primary" data-testid="md-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</button>
        </div>
      )}
    </form>
  );
}
